import { CustomerSupportSession, TabData } from '../types';

export interface CallData {
    callUuid: string;
    parentCallUuid?: string;
    customerNumber?: string;
    timestamp?: number;
}

export const getCurrentCallData = (): CallData | null => {
    try {
        const currentCallData = localStorage.getItem('currentCallDialingEvent');
        if (!currentCallData) return null;

        return JSON.parse(currentCallData);
    } catch (error) {
        console.error('Error parsing call data:', error);
        return null;
    }
};

export const saveSessionTabs = (session: Partial<CustomerSupportSession>) => {
    try {
        const state = localStorage.getItem('customerSupportSession');
        const parsedState: CustomerSupportSession[] = state ? JSON.parse(state) : [];

        // Find session by looping through array and comparing IDs
        let existingSessionIndex = -1;

        // Get current session identifiers from localStorage
        const currentCallData = localStorage.getItem('currentCallDialingEvent');
        const chatSessionData = localStorage.getItem('omnichannel_chat_session');

        let currentCallId: string | undefined;
        let currentChatId: number | undefined;

        if (currentCallData) {
            try {
                const callData = JSON.parse(currentCallData);
                currentCallId = callData.parentCallUuid || callData.callUuid;
            } catch (error) {
                console.error('Error parsing call data:', error);
            }
        }

        if (chatSessionData) {
            try {
                const { chatSessionId } = JSON.parse(chatSessionData);
                currentChatId = Number(chatSessionId);
            } catch (error) {
                console.error('Error parsing chat data:', error);
            }
        }

        // Loop through sessions to find matching one - use type-safe comparisons
        for (let i = 0; i < parsedState.length; i++) {
            const existingSession = parsedState[i];

            // Check if this is a call session match - compare as strings
            if (session.callId && existingSession.callId &&
                String(session.callId) === String(existingSession.callId)) {
                existingSessionIndex = i;
                break;
            }

            // Check if this is a chat session match - compare as numbers
            if (session.chatSessionId && existingSession.chatSessionId &&
                Number(session.chatSessionId) === Number(existingSession.chatSessionId)) {
                existingSessionIndex = i;
                break;
            }

            // Check against current localStorage IDs if session doesn't specify
            if (!session.callId && !session.chatSessionId) {
                if (currentCallId && existingSession.callId &&
                    String(currentCallId) === String(existingSession.callId)) {
                    existingSessionIndex = i;
                    break;
                }
                if (currentChatId && existingSession.chatSessionId &&
                    Number(currentChatId) === Number(existingSession.chatSessionId)) {
                    existingSessionIndex = i;
                    break;
                }
            }
        }

        // ...existing merge logic...
        if (existingSessionIndex >= 0) {
            const existingSession = parsedState[existingSessionIndex];
            let mergedSearchData: TabData[] = [];

            if (session.searchData) {
                mergedSearchData = session.searchData.map((newTab) => {
                    const existingTab = existingSession.searchData?.find(tab => tab.key === newTab.key);

                    if (existingTab) {
                        const existingModals = existingTab.minimizedModals || [];
                        const newModals = newTab.minimizedModals || [];
                        const mergedModals = Array.from(new Set([...existingModals, ...newModals]));

                        return {
                            ...existingTab,
                            ...newTab,
                            minimizedModals: mergedModals,
                            formData: {
                                ...(existingTab.formData || {}),
                                ...(newTab.formData || {})
                            },
                            searchCriteria: (newTab.searchCriteria && Object.keys(newTab.searchCriteria).length > 0)
                                ? newTab.searchCriteria
                                : (existingTab.searchCriteria && Object.keys(existingTab.searchCriteria).length > 0)
                                    ? existingTab.searchCriteria
                                    : newTab.searchCriteria || {}
                        };
                    }

                    return newTab;
                });
            } else if (existingSession.searchData) {
                mergedSearchData = [...existingSession.searchData];
            }

            // Only preserve existing tabs if we're not getting a full update
            if (existingSession.searchData && (!session.searchData || session.searchData.length === 0)) {
                existingSession.searchData.forEach(existingTab => {
                    const tabExists = mergedSearchData.some(tab => tab.key === existingTab.key);
                    if (!tabExists) {
                        mergedSearchData.push(existingTab);
                    }
                });
            }

            parsedState[existingSessionIndex] = {
                ...existingSession,
                ...session,
                contactResponse: session.contactResponse || existingSession.contactResponse,
                searchData: mergedSearchData,
                globalSearchCriteria: session.globalSearchCriteria || existingSession.globalSearchCriteria
            };

        } else {
            const sessionToSave: CustomerSupportSession = {
                ...session,
                searchData: session.searchData?.map(tab => ({
                    ...tab,
                    minimizedModals: Array.from(new Set(tab.minimizedModals || []))
                }))
            } as CustomerSupportSession;

            parsedState.push(sessionToSave);
        }

        localStorage.setItem('customerSupportSession', JSON.stringify(parsedState));
    } catch (error) {
        console.error('Error saving session tabs:', error);
    }
};

/**
 * Gets the global search criteria for a session
 */
export const getSessionSearchCriteria = (sessionId: string | number | undefined): any => {
  if (!sessionId) return {};
  
  try {
    const storedSessions = localStorage.getItem('customerSupportSession');
    if (!storedSessions) return {};
    
    const sessions = JSON.parse(storedSessions);
    const sessionIndex = sessions.findIndex((s: any) =>
      (typeof sessionId === 'string' && s.callId === sessionId) ||
      (typeof sessionId === 'number' && s.chatSessionId === sessionId)
    );
    
    if (sessionIndex >= 0) {
      const session = sessions[sessionIndex];
      
      // Return globalSearchCriteria if available
      if (session.globalSearchCriteria) {
        return session.globalSearchCriteria;
      }
      
      // ✅ ADD: Fallback to searchCriteria from the first tab if available
      if (session.searchData && session.searchData.length > 0) {
        const firstTabWithCriteria = session.searchData.find((tab: any) => 
          tab.searchCriteria && Object.keys(tab.searchCriteria).length > 0
        );
        
        return firstTabWithCriteria?.searchCriteria || {};
      }
    }
  } catch (error) {
    console.error('Error getting session search criteria:', error);
  }
  
  return {};
};

export const saveGlobalSearchCriteria = (sessionId: string | number | undefined, criteria: any) => {
    if (!sessionId) return;

    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);
        const sessionIndex = sessions.findIndex((s: any) =>
            s.callId === sessionId || s.chatSessionId === sessionId
        );

        if (sessionIndex >= 0) {
            const session = sessions[sessionIndex];

            // Update global search criteria
            session.globalSearchCriteria = criteria;

            // ✅ ADD: Update searchCriteria in tabs but preserve formData and minimizedModals
            if (session.searchData) {
                session.searchData = session.searchData.map((tab: any) => {
                    return {
                        ...tab,
                        searchCriteria: criteria
                    };
                });
            }

            localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
        }
    } catch (error) {
        console.error('Error saving global search criteria:', error);
    }
};

/**
 * Cleans up completed or stale chat sessions from localStorage
 */
export const cleanupStaleSessions = () => {
    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);

        // Get current active chat session ID
        let activeChatSessionId: number | undefined;
        const chatSessionData = localStorage.getItem('omnichannel_chat_session');
        if (chatSessionData) {
            try {
                activeChatSessionId = JSON.parse(chatSessionData).chatSessionId;
            } catch (error) {
                console.error('Error parsing active chat session:', error);
            }
        }

        // Filter out sessions that are no longer active (except the active chat session)
        const filteredSessions = sessions.filter((session: any) => {
            // Keep all call sessions
            if (session.callId) return true;

            // Keep the active chat session
            if (session.chatSessionId && session.chatSessionId === activeChatSessionId) {
                return true;
            }

            // For chat sessions, check if they exist in available chats from API
            // This would require additional logic to track active chats from the API

            // For now, we'll assume any chat session not matching the active one should be removed
            return false;
        });

        // Save filtered sessions back to localStorage
        if (filteredSessions.length !== sessions.length) {
            localStorage.setItem('customerSupportSession', JSON.stringify(filteredSessions));
        }
    } catch (error) {
        console.error('Error cleaning up stale sessions:', error);
    }
};

// ✅ ADD: New helper function to safely update session data
export const safeUpdateSession = (sessionId: string | number | undefined, updater: (session: any) => void) => {
    if (!sessionId) return;

    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);
        const sessionIndex = sessions.findIndex((s: any) =>
            (typeof sessionId === 'string' && s.callId === sessionId) ||
            (typeof sessionId === 'number' && s.chatSessionId === sessionId)
        );

        if (sessionIndex >= 0) {
            // Get a copy of the session
            const session = { ...sessions[sessionIndex] };

            // Apply the update function
            updater(session);

            // Update the session in the array
            sessions[sessionIndex] = session;

            // Save back to localStorage
            localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
            return true;
        }
    } catch (error) {
        console.error('Error in safeUpdateSession:', error);
    }

    return false;
};

/**
 * Merge a new session with existing data, preserving critical fields
 */
export const mergeSessionData = (existingSession: any, newSession: any) => {
    if (!existingSession) return newSession;

    const merged = { ...existingSession };

    // Update searchData carefully to preserve formData and minimizedModals
    if (newSession.searchData) {
        if (!merged.searchData) {
            merged.searchData = newSession.searchData;
        } else {
            // Map of existing tabs by key for easy lookup
            const existingTabs = merged.searchData.reduce((acc: any, tab: any) => {
                acc[tab.key] = tab;
                return acc;
            }, {});

            // Update or add new tabs
            merged.searchData = newSession.searchData.map((newTab: any) => {
                const existingTab = existingTabs[newTab.key];
                if (!existingTab) return newTab;

                return {
                    ...existingTab, // Start with existing properties
                    ...newTab,      // Overlay with new properties from UI state
                    // Prioritize newTab (UI state) for formData and minimizedModals.
                    // Fall back to existingTab (localStorage) if newTab doesn't have them, then to default.
                    formData: (newTab.formData !== undefined) ? newTab.formData : (existingTab.formData || {}),
                    minimizedModals: (newTab.minimizedModals !== undefined) ? newTab.minimizedModals : (existingTab.minimizedModals || []),
                    // searchCriteria should also prioritize newTab or merge as intended
                    searchCriteria: newTab.searchCriteria || existingTab.searchCriteria || {}
                };
            });
        }
    }

    // Update other fields
    if (newSession.globalSearchCriteria) {
        merged.globalSearchCriteria = newSession.globalSearchCriteria;
    }

    if (newSession.contactResponse) {
        merged.contactResponse = newSession.contactResponse;
    }

    if (newSession.type) {
        merged.type = newSession.type;
    }

    if (newSession.originTabKey) {
        merged.originTabKey = newSession.originTabKey;
    }

    return merged;
};

/**
 * Improved version of saveSessionTabs that better preserves form data
 */
export const saveSessionTabsWithFormPreservation = (session: Partial<CustomerSupportSession>) => {
    try {
        const state = localStorage.getItem('customerSupportSession');
        const parsedState: CustomerSupportSession[] = state ? JSON.parse(state) : [];

        // Find session by looping through array and comparing IDs
        let existingSessionIndex = -1;

        // Get current session identifiers from localStorage
        const currentCallData = localStorage.getItem('currentCallDialingEvent');
        const chatSessionData = localStorage.getItem('omnichannel_chat_session');

        let currentCallId: string | undefined;
        let currentChatId: number | undefined;

        if (currentCallData) {
            try {
                const callData = JSON.parse(currentCallData);
                currentCallId = callData.parentCallUuid || callData.callUuid;
            } catch (error) {
                console.error('Error parsing call data:', error);
            }
        }

        if (chatSessionData) {
            try {
                const { chatSessionId } = JSON.parse(chatSessionData);
                currentChatId = Number(chatSessionId);
            } catch (error) {
                console.error('Error parsing chat data:', error);
            }
        }

        // Loop through sessions to find matching one - use type-safe comparisons
        for (let i = 0; i < parsedState.length; i++) {
            const existingSession = parsedState[i];

            // Check if this is a call session match - compare as strings
            if (session.callId && existingSession.callId &&
                String(session.callId) === String(existingSession.callId)) {
                existingSessionIndex = i;
                break;
            }

            // Check if this is a chat session match - compare as numbers
            if (session.chatSessionId && existingSession.chatSessionId &&
                Number(session.chatSessionId) === Number(existingSession.chatSessionId)) {
                existingSessionIndex = i;
                break;
            }

            // Check against current localStorage IDs if session doesn't specify
            if (!session.callId && !session.chatSessionId) {
                if (currentCallId && existingSession.callId &&
                    String(currentCallId) === String(existingSession.callId)) {
                    existingSessionIndex = i;
                    break;
                }
                if (currentChatId && existingSession.chatSessionId &&
                    Number(currentChatId) === Number(existingSession.chatSessionId)) {
                    existingSessionIndex = i;
                    break;
                }
            }
        }

        if (existingSessionIndex >= 0) {
            // Use the improved merge function
            parsedState[existingSessionIndex] = mergeSessionData(
                parsedState[existingSessionIndex],
                session
            );

        } else {
            const sessionToSave: CustomerSupportSession = {
                ...session,
                searchData: session.searchData?.map(tab => ({
                    ...tab,
                    minimizedModals: Array.from(new Set(tab.minimizedModals || []))
                }))
            } as CustomerSupportSession;

            parsedState.push(sessionToSave);
        }

        localStorage.setItem('customerSupportSession', JSON.stringify(parsedState));
    } catch (error) {
        console.error('Error saving session tabs:', error);
    }
};
